/**
 * Admin Panel Permission Types
 *
 * Shared types for admin panel permission system.
 * Used by both frontend and backend.
 */

/**
 * Permission action types (maps to RWX)
 */
export type AdminPermissionAction = "read" | "write" | "delete";

/**
 * Permission constants (chmod-style)
 */
export const AdminPermission = {
  NONE: 0,
  READ: 1,
  WRITE: 2,
  DELETE: 4,
  READ_WRITE: 3,
  READ_DELETE: 5,
  WRITE_DELETE: 6,
  FULL: 7
} as const;

/**
 * ROOT policy constants
 * The ROOT policy is "godmode" - full access to everything.
 * Used for fail-secure checks (unconfigured routes only allow ROOT).
 */
export const ROOT_POLICY_ID = "00000000-0000-0000-0000-000000000001";
export const ROOT_POLICY_NAME = "ROOT";

export type AdminPermissionValue = (typeof AdminPermission)[keyof typeof AdminPermission];

/**
 * Admin resource type
 */
export type AdminResourceType = "NAV" | "BLOCK" | "ACTION";

/**
 * Admin audit action
 */
export type AdminAuditAction = "READ" | "WRITE" | "DELETE";

/**
 * Policy rule
 */
export interface AdminPolicyRule {
  resource: string;
  permission: number;
}

/**
 * Policy profile
 */
export interface AdminPolicy {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isEditable: boolean;
  rules: AdminPolicyRule[];
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * User override
 */
export interface AdminUserOverride {
  id: string;
  resource: string;
  permission: number;
  reason: string | null;
  expiresAt: string | null;
}

/**
 * Admin user with permissions.
 *
 * `policyGrants` is the user's RAW policy-rule map (`resource → permission`) — it is
 * deliberately NOT pre-resolved or catalog-bounded. Callers resolve a concrete
 * resource with {@link resolveAdminPermission}, passing `policyGrants` as the rules
 * and the active `overrides` separately, so the frontend and backend guard produce
 * identical decisions.
 */
export interface AdminUserWithPermissions {
  id: string;
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  policy: {
    id: string;
    name: string;
    isSystem: boolean;
    isEditable: boolean;
  };
  overrides: AdminUserOverride[];
  policyGrants?: Record<string, number>;
}

/**
 * Admin resource for registry
 */
export interface AdminResource {
  id: string;
  path: string;
  name: string;
  description: string | null;
  category: string | null;
  resourceType: AdminResourceType;
  parentPath: string | null;
  sortOrder: number;
}

/**
 * Admin resource tree node for visual picker
 */
export interface AdminResourceTree extends Omit<AdminResource, "parentPath"> {
  children: AdminResourceTree[];
}

/**
 * Permission check result from backend
 */
export interface AdminPermissionCheckResult {
  allowed: boolean;
  permission: number;
  resource: string;
  action: AdminPermissionAction;
  source: "policy" | "override" | "cascade" | "denied";
  matchedResource?: string;
}

/**
 * Current user's admin status response
 */
export interface AdminStatusResponse {
  isAdmin: boolean;
  user: AdminUserWithPermissions | null;
  resources: AdminResource[];
}

/**
 * Audit log entry
 */
export interface AdminAuditLogEntry {
  id: string;
  adminUserId: string | null;
  userEmail: string | null;
  action: AdminAuditAction;
  resource: string;
  resourceId: string | null;
  meta: Record<string, unknown>;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

/**
 * Paginated audit logs response
 */
export interface AdminAuditLogsResponse {
  logs: AdminAuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Admin user summary for filter dropdown
 */
export interface AdminUserSummary {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Export format options
 */
export type AuditExportFormat = "csv" | "json";

/**
 * User activity response
 */
export interface UserActivityResponse {
  user: AdminUserSummary;
  logs: AdminAuditLogEntry[];
  stats: {
    totalActions: number;
    byAction: Record<string, number>;
    byResource: { resource: string; count: number }[];
  };
}

/**
 * Resource activity response
 */
export interface ResourceActivityResponse {
  resource: string;
  logs: AdminAuditLogEntry[];
  stats: {
    totalActions: number;
    byAction: Record<string, number>;
    byUser: { email: string; count: number }[];
  };
}

/**
 * Helper functions
 */
export function canRead(permission: number): boolean {
  return (permission & AdminPermission.READ) !== 0;
}

export function canWrite(permission: number): boolean {
  return (permission & AdminPermission.WRITE) !== 0;
}

export function canDelete(permission: number): boolean {
  return (permission & AdminPermission.DELETE) !== 0;
}

export function hasPermission(permission: number, action: AdminPermissionAction): boolean {
  switch (action) {
    case "read":
      return canRead(permission);
    case "write":
      return canWrite(permission);
    case "delete":
      return canDelete(permission);
    default:
      return false;
  }
}

export function formatPermission(permission: number): string {
  const r = canRead(permission) ? "R" : "-";
  const w = canWrite(permission) ? "W" : "-";
  const x = canDelete(permission) ? "X" : "-";
  return `${r}${w}${x}`;
}

/**
 * Resource path separator and wildcard token. A resource path is colon-delimited
 * (`admin:users:list`); a `*` in the final segment matches everything below its
 * parent (`admin:users:*`, `admin:*`).
 */
export const ADMIN_RESOURCE_SEPARATOR = ":";
export const ADMIN_RESOURCE_WILDCARD = "*";

/**
 * Outcome of resolving a permission for a resource.
 * - `policy`: matched a rule on the exact resource
 * - `cascade`: matched a rule on an ancestor — a wildcard (`admin:users:*`, `admin:*`) or a bare parent node (`admin:users`)
 * - `override`: matched a per-user override (at any hierarchy level)
 */
export interface AdminPermissionResolution {
  permission: number;
  source: "policy" | "override" | "cascade";
  matchedResource: string;
}

/**
 * Build the resolution hierarchy for a resource: the exact path first, then each
 * ancestor from most to least specific. Every intermediate ancestor contributes
 * two cascade sources — the wildcard form and the bare parent node itself:
 *
 * @example
 * generateResourceHierarchy("admin:users:list")
 * // → ["admin:users:list", "admin:users:*", "admin:users", "admin:*"]
 *
 * The wildcard ranks ahead of the bare parent at the same level: `admin:users:*`
 * is an explicit "everything under users" grant, more intentional than the
 * implicit cascade from a grant on the `admin:users` node, so it wins on a tie.
 *
 * **Bare-parent cascade** means a grant on a parent NODE covers its descendants:
 * granting `admin:organizations` now also grants `admin:organizations:list`. It
 * applies only to real nodes — `admin:x` and deeper. The root stays expressible
 * ONLY as the `admin:*` wildcard, never a bare `admin`, so there is exactly one
 * way to say "godmode" and a stray single-segment rule can never mean it. A
 * two-segment resource (`admin:echo`) has no parent node between it and the root,
 * so its hierarchy is unchanged: `["admin:echo", "admin:*"]`.
 */
export function generateResourceHierarchy(resource: string): string[] {
  const hierarchy: string[] = [resource];
  const parts = resource.split(ADMIN_RESOURCE_SEPARATOR);

  for (let i = parts.length - 1; i > 0; i--) {
    const prefix = parts.slice(0, i);
    const wildcardPath = [...prefix, ADMIN_RESOURCE_WILDCARD].join(ADMIN_RESOURCE_SEPARATOR);
    if (wildcardPath !== resource) {
      hierarchy.push(wildcardPath);
    }
    // Bare-parent cascade, real nodes only (≥ 2 segments). The root level (i === 1,
    // a single "admin" segment) is intentionally skipped — godmode is `admin:*` alone.
    if (i >= 2) {
      hierarchy.push(prefix.join(ADMIN_RESOURCE_SEPARATOR));
    }
  }

  return hierarchy;
}

/**
 * Resolve the effective permission for a resource from a user's policy grants and
 * active overrides. This is the single source of truth for admin authorization —
 * the backend guard and the frontend both call it, so "visible in the UI" and
 * "authorized by the API" can never diverge.
 *
 * Precedence mirrors chmod-style cascading with overrides on top: overrides are
 * checked across the WHOLE hierarchy (exact → wildcard + bare-parent ancestors) before any rule.
 * A wildcard deny-override (`admin:*` = 0) therefore revokes a more specific grant
 * (`admin:users` = 7), exactly as the backend enforces it.
 *
 * Inputs are `resource → permission` maps (build them with {@link buildPermissionMap}).
 * Returns `null` when nothing in the hierarchy matches — callers treat that as no
 * access ({@link AdminPermission.NONE}).
 *
 * @example
 * resolveAdminPermission("admin:users:list", { "admin:users:*": 7 }, {})
 * // → { permission: 7, source: "cascade", matchedResource: "admin:users:*" }
 */
export function resolveAdminPermission(
  resource: string,
  rules: Readonly<Record<string, number>>,
  overrides: Readonly<Record<string, number>> = {}
): AdminPermissionResolution | null {
  const hierarchy = generateResourceHierarchy(resource);

  // Overrides win at any level — check the whole hierarchy before any rule.
  for (const path of hierarchy) {
    const overridden = overrides[path];
    if (overridden !== undefined) {
      return { permission: overridden, source: "override", matchedResource: path };
    }
  }

  // Then policy rules, most specific first.
  for (const path of hierarchy) {
    const granted = rules[path];
    if (granted !== undefined) {
      return {
        permission: granted,
        source: path === resource ? "policy" : "cascade",
        matchedResource: path
      };
    }
  }

  return null;
}

/**
 * Resolve a resource to its numeric permission value (0 when nothing matches).
 * Convenience wrapper over {@link resolveAdminPermission} for the common "how much
 * access does this user have here" question.
 *
 * @example
 * hasPermission(getAdminPermissionValue("admin:echo", rules, overrides), "read")
 */
export function getAdminPermissionValue(
  resource: string,
  rules: Readonly<Record<string, number>>,
  overrides: Readonly<Record<string, number>> = {}
): number {
  return resolveAdminPermission(resource, rules, overrides)?.permission ?? AdminPermission.NONE;
}

/**
 * Reduce a list of `{ resource, permission }` grants (policy rules or overrides)
 * to a `resource → permission` lookup map. Later entries win on duplicate keys.
 */
export function buildPermissionMap(
  entries: ReadonlyArray<{ resource: string; permission: number }>
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const entry of entries) {
    map[entry.resource] = entry.permission;
  }
  return map;
}

/**
 * Whether an override is currently in effect. A `null`/absent `expiresAt` never
 * expires; otherwise the override is active only while `expiresAt` is in the future.
 * Accepts an ISO string (API payloads) or a `Date` (backend entities).
 */
export function isOverrideActive(
  expiresAt: string | Date | null | undefined,
  now: Date = new Date()
): boolean {
  if (expiresAt === null || expiresAt === undefined) {
    return true;
  }
  const expiry = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  return expiry.getTime() > now.getTime();
}
